import * as React from 'react';
import { labelOf, toneOf } from '@mail-sv/shared';
import { AlertTriangle, Check, Clock, Loader2, MinusCircle, RotateCcw, X } from 'lucide-react';
import { cn, formatDateTime } from '../lib/utils.js';
import { StatusBadge } from './status-badge.js';

/**
 * プロビジョニングジョブの進行を縦のタイムラインで見せる。
 *
 * 契約者パネルでも運営コンソールでも使うが、**契約者に見せてよいのは
 * `userMessage` だけ**（`GET /api/v1/portal/jobs/:jobId` は state / progress /
 * userMessage しか返さない）。内部エラー本文を渡す運用にしないこと。
 */

export interface JobTimelineStep {
  id: string;
  /** `mailbox_create` のような JOB_KIND / ステップ名。日本語化は `labelOf`。 */
  name: string;
  /** JOB_STEP_STATUS の値。 */
  status: string;
  startedAt?: string | Date | null;
  finishedAt?: string | Date | null;
  /** 画面に出してよい説明。契約者向けには `userMessage` を入れる。 */
  message?: React.ReactNode;
  /** 再試行回数。0 なら出さない。 */
  attempt?: number;
}

const STEP_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  pending: Clock,
  queued: Clock,
  running: Loader2,
  waiting: Clock,
  succeeded: Check,
  failed: X,
  skipped: MinusCircle,
  canceled: MinusCircle,
  compensating: RotateCcw,
  compensated: RotateCcw,
};

const TONE_RING: Record<string, string> = {
  success: 'border-success/40 bg-success/15 text-success',
  warning: 'border-warning/40 bg-warning/15 text-warning',
  destructive: 'border-destructive/40 bg-destructive/15 text-destructive',
  info: 'border-info/40 bg-info/15 text-info',
  muted: 'border-border bg-muted text-muted-foreground',
  default: 'border-border bg-muted text-muted-foreground',
};

export interface JobTimelineProps {
  steps: readonly JobTimelineStep[];
  /** ジョブ全体の状態。ヘッダのバッジに出す。 */
  jobStatus?: string;
  /** 0..1。省略時はステップの成功率から計算する。 */
  progress?: number;
  className?: string;
}

/**
 * 0..1 に丸めた進捗。**表示（バーの幅）と読み上げ（`aria-valuenow`）で
 * 必ず同じ値を使う。**
 *
 * 以前はバーだけ `Math.min(Math.max(ratio, 0), 1)` で挟み、`aria-valuenow` は
 * 生の `ratio * 100` を出していた。`aria-valuemax={100}` なのに 4000 が出る、
 * つまり**目で見える進捗と読み上げられる進捗が食い違う**状態で、
 * 画面を見ている限り誰も気付けない。
 *
 * 範囲外の値は開発中に 1 度だけ警告する（`data-table.tsx` の
 * `warnIfTooManyColumns` と同じ形）。`progress` は **0..1 の比** であって
 * パーセントではない — 実際に `provisioning_jobs.progress`（0..100 の整数）を
 * そのまま渡している呼び出し側があると、バーは 1% でも 100% に振り切れる。
 */
let warnedOutOfRangeProgress = false;

function clampProgress(ratio: number): number {
  if (!Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    if (!warnedOutOfRangeProgress) {
      warnedOutOfRangeProgress = true;
      console.warn(
        `[JobTimeline] progress は 0〜1 の比で渡してください（受け取った値: ${String(ratio)}）。` +
          'パーセント（0〜100）や NaN を渡すと、バーが振り切れたまま実際の進捗が読めなくなります。',
      );
    }
  }
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(Math.max(ratio, 0), 1);
}

export function JobTimeline({ steps, jobStatus, progress, className }: JobTimelineProps) {
  const done = steps.filter((s) => s.status === 'succeeded' || s.status === 'skipped').length;
  // `??` であること（`||` にすると progress=0 が「未指定」に落ちて、
  // 開始直後のジョブがステップの成功率で 0 以外に見える）。
  const ratio = clampProgress(progress ?? (steps.length > 0 ? done / steps.length : 0));

  return (
    <div className={cn('space-y-4', className)}>
      {jobStatus ? (
        <div className="flex items-center justify-between gap-3">
          <StatusBadge status={jobStatus} dot pulse={jobStatus === 'running'} />
          <span className="tabular text-xs text-muted-foreground">
            {done} / {steps.length} ステップ完了
          </span>
        </div>
      ) : null}

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={Math.round(ratio * 100)} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={cn(
            'h-full rounded-full transition-all',
            jobStatus === 'failed' ? 'bg-destructive' : 'bg-primary',
          )}
          style={{ width: `${ratio * 100}%` }}
        />
      </div>

      <ol className="relative space-y-0">
        {steps.map((step, i) => {
          const tone = toneOf(step.status);
          const Icon = STEP_ICON[step.status] ?? Clock;
          const isLast = i === steps.length - 1;
          return (
            <li key={step.id} className="relative flex gap-3 pb-4 last:pb-0">
              {/* 縦線。最後のステップでは引かない。 */}
              {!isLast ? (
                <span
                  aria-hidden
                  className="absolute left-[13px] top-7 h-[calc(100%-1.75rem)] w-px bg-border"
                />
              ) : null}
              <span
                className={cn(
                  'relative z-10 flex size-7 shrink-0 items-center justify-center rounded-full border',
                  TONE_RING[tone] ?? TONE_RING.default,
                )}
              >
                <Icon className={cn('size-3.5', step.status === 'running' && 'animate-spin')} />
              </span>
              <div className="min-w-0 flex-1 space-y-0.5 pt-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{labelOf(step.name)}</span>
                  <StatusBadge status={step.status} size="sm" />
                  {step.attempt && step.attempt > 1 ? (
                    <span className="inline-flex items-center gap-1 text-xs text-warning">
                      <AlertTriangle className="size-3" aria-hidden />
                      {step.attempt} 回目の試行
                    </span>
                  ) : null}
                </div>
                {step.message ? (
                  <p className="break-words text-sm text-muted-foreground">{step.message}</p>
                ) : null}
                {step.startedAt || step.finishedAt ? (
                  <p className="tabular text-xs text-muted-foreground">
                    {step.startedAt ? formatDateTime(step.startedAt) : '—'}
                    {step.finishedAt ? ` → ${formatDateTime(step.finishedAt)}` : ''}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
